import { z } from 'zod';
/**
 * Contrats de l'ARBRE DES DOSSIERS (XPHOTO-32, lot 3 — amendé).
 * Source unique de vérité front + back.
 *
 * ===========================================================================
 * POURQUOI CE CONTRAT EXISTE
 * ===========================================================================
 * Dans xphoto un dossier n'a pas d'identité propre : son identité EST son chemin
 * (cf. `ADR-0014`). Historiquement la liste des dossiers était DÉRIVÉE du chemin des
 * assets (`assets.dir_path`, colonne générée, migration `0043`), avec une conséquence
 * déjà constatée par l'outil « Nettoyer les dossiers vides » : **un répertoire vide
 * était totalement invisible dans l'application** — il existait sur disque, personne
 * ne le voyait. Tant que c'était vrai, « créer un dossier » n'avait aucun sens.
 *
 * Le lot 3 a levé cette invisibilité en lisant l'arbre EN DIRECT sur le disque. Ce
 * choix a été RENVERSÉ (amendement d'`ADR-0013`, migration `0046`) : les répertoires
 * sont désormais un INDEX alimenté par le scan (`library_folders`), exactement comme
 * les photos. **Le DISQUE reste l'autorité, la base en est l'index.** Prix assumé : un
 * dossier créé hors de l'application attend le prochain scan — comme une photo.
 *
 * ===========================================================================
 * DEUX SURFACES, LA MÊME SOURCE
 * ===========================================================================
 *  - `GET /api/libraries/:id/folder-tree?parent=<chemin>` → `FolderTreeResult`.
 *    Navigation NIVEAU PAR NIVEAU de l'onglet Dossiers : les enfants DIRECTS lus par
 *    ÉGALITÉ INDEXÉE (`library_folders.parent_path`), FUSIONNÉS avec les facettes SQL
 *    (compteurs + couverture). C'est le chemin CHAUD (chaque clic dans l'arbre).
 *  - `GET /api/libraries/:id/all-folders` → `FolderListResult` (`organize.ts`,
 *    contrat INCHANGÉ) : sélecteur d'arbre de destination, un simple balayage de
 *    l'index. Il reste mis en cache côté client (`organize/use-all-folders.ts`).
 *
 * ⚠ NE JAMAIS remplacer la source des COMPTEURS par un parcours disque. La migration
 * `0043` a dénormalisé `dir_path` en colonne INDEXÉE précisément parce que dériver le
 * répertoire coûtait 782 ms par ouverture de dossier sur 50 000 assets (0,07 ms
 * après). L'index dit QUELS répertoires existent ; SQL dit ce qu'ils contiennent.
 *
 * ⚠ ET NE JAMAIS BRANCHER UNE SUPPRESSION SUR CET INDEX. Le chemin d'effacement
 * (`organize/folder-delete.ts`) RE-INSPECTE le système de fichiers avant tout
 * `rmdir`/`unlink` : un index périmé ne doit pas pouvoir autoriser une destruction.
 */
// ---------------------------------------------------------------------------
// Répertoires EXCLUS du listage (règle PURE, partagée front + back)
// ---------------------------------------------------------------------------
/**
 * Un répertoire CACHÉ (nom commençant par un point) n'est ni parcouru ni listé.
 *
 * SOURCE UNIQUE de cette règle, qui préexiste à ce lot : elle est celle de l'outil
 * « Nettoyer les dossiers vides » (`apps/api/src/organize/empty-folders.ts`, où elle
 * était une fonction PRIVÉE `isHiddenName`), désormais remontée ici pour que le
 * listage et le nettoyage ne puissent pas diverger. Un `.stfolder` ou un
 * `.stversions` (Syncthing) est un MARQUEUR d'outil de synchronisation, pas un
 * dossier de photos : le montrer dans l'arbre n'aurait aucun sens, et le proposer
 * comme destination serait une invitation à casser la synchronisation.
 */
export function isHiddenFolderName(name) {
    return name.startsWith('.');
}
/**
 * Répertoires NON cachés néanmoins exclus du LISTAGE, par nom EXACT (comparaison en
 * minuscules — Windows et macOS ignorent la casse). Ce sont des répertoires semés par
 * un système de fichiers ou un NAS, jamais par l'utilisateur :
 *
 *  - `@eaDir`                    : vignettes/index Synology DSM ;
 *  - `#recycle`                  : corbeille Synology ;
 *  - `$RECYCLE.BIN`              : corbeille Windows ;
 *  - `System Volume Information` : métadonnées de volume Windows ;
 *  - `lost+found`                : récupération de fsck (ext4).
 *
 * ⚠ PORTÉE STRICTEMENT LIMITÉE À L'ARBRE DES DOSSIERS. Cette liste ne rend RIEN
 * supprimable et n'entre PAS dans le nettoyage des dossiers vides : `ADR-0010` borne
 * l'unique `unlink` du produit à la liste blanche `SYSTEM_FILENAMES`, et cette
 * garantie reste intacte. Elle dit seulement « ne montre pas ça dans l'arbre ».
 *
 * Depuis que l'arbre est un INDEX alimenté par le scan (migration `0046`, amendement
 * d'`ADR-0013`), la règle s'applique À L'INDEXATION : ces répertoires n'entrent jamais
 * dans `library_folders`. C'est le même périmètre qu'avant — « ne pas figurer dans
 * l'arbre » — appliqué une fois à l'écriture plutôt qu'à chaque lecture. Les PHOTOS
 * qu'ils contiennent restent indexées comme avant : le scanner ne filtre toujours
 * AUCUN répertoire pour les fichiers (les exclure ferait disparaître de la galerie des
 * médias aujourd'hui visibles).
 *
 * HONNÊTETÉ SUR L'ANTÉRIORITÉ : contrairement à la règle des répertoires cachés,
 * ces noms n'étaient exclus NULLE PART avant le lot 3. C'est une règle NOUVELLE,
 * introduite pour le seul affichage de l'arbre.
 */
export const EXCLUDED_FOLDER_NAMES = [
    '@eaDir',
    '#recycle',
    '$RECYCLE.BIN',
    'System Volume Information',
    'lost+found',
];
/** Index minuscule d'`EXCLUDED_FOLDER_NAMES` (comparaison insensible à la casse). */
const EXCLUDED_FOLDER_NAMES_LOWER = new Set(EXCLUDED_FOLDER_NAMES.map((n) => n.toLowerCase()));
/**
 * Ce répertoire doit-il être IGNORÉ par le listage de l'arbre (ni montré, ni
 * parcouru) ? PURE, partagée front + back : le serveur s'en sert pour filtrer, la
 * SPA pour ne jamais proposer un tel nom à la création (lot 4).
 */
export function isExcludedFolderName(name) {
    return isHiddenFolderName(name) || EXCLUDED_FOLDER_NAMES_LOWER.has(name.toLowerCase());
}
// ---------------------------------------------------------------------------
// Un répertoire de l'arbre (disque ⋈ facettes SQL)
// ---------------------------------------------------------------------------
/**
 * Un SOUS-RÉPERTOIRE DIRECT du dossier courant, tel que le voit l'onglet Dossiers.
 *
 * Les trois natures d'information sont DÉLIBÉRÉMENT distinctes — c'est tout l'objet
 * de ce lot, et le CRUD du lot 4 s'appuie dessus :
 *
 *  - `indexed`       : le RÉPERTOIRE figure dans l'INDEX DES DOSSIERS
 *    (`library_folders`), c'est-à-dire que le dernier scan l'a vu sur le disque.
 *    `false` = le dossier n'apparaît QUE parce que des médias le référencent
 *    (`assets.dir_path`) : soit c'est un répertoire EXCLU du listage (`.stversions`,
 *    `@eaDir`… — le scan indexe leurs photos mais n'indexe pas ces répertoires), soit
 *    l'index des dossiers est en retard sur celui des photos. On le montre quand même,
 *    parce que ses photos, elles, sont listables — le cacher rendrait des médias
 *    inatteignables par la navigation.
 *
 *    ⚠ CE CHAMP S'APPELAIT `onDisk` ET NE DIT PLUS LA MÊME CHOSE. Tant que l'arbre se
 *    lisait EN DIRECT sur le disque (lot 3), `onDisk: false` signifiait « le
 *    répertoire n'est plus là, MAINTENANT ». Depuis que l'arbre est un index
 *    (amendement d'`ADR-0013`), l'application ne sait plus rien du disque entre deux
 *    scans : un répertoire effacé de l'extérieur reste `indexed: true` jusqu'au scan
 *    suivant. Renommer le champ plutôt que de laisser l'ancien nom mentir.
 *  - `count`         : nombre d'assets INDEXÉS (présents, hors corbeille) du
 *    SOUS-ARBRE de ce répertoire. Sémantique STRICTEMENT identique à
 *    `FolderFacet.count` (`asset-query.ts`) — c'est la même valeur, issue de la même
 *    requête. `0` = aucun asset indexé, c'est-à-dire le cas nominal d'un répertoire
 *    VIDE, celui que l'application ne savait pas montrer avant le lot 3.
 *  - `hasSubfolders` : le répertoire contient AU MOINS un sous-répertoire INDEXÉ.
 *    Répond à « peut-on descendre ? » sans que le client ait à sonder chaque niveau, y
 *    compris quand `count` vaut 0 (une branche entièrement vide reste explorable).
 *
 * `coverAssetId` / `latestTakenAt` viennent aussi des facettes et valent `null` pour
 * un répertoire sans asset indexé (la carte affiche alors son placeholder).
 *
 * `path` est le chemin RELATIF POSIX COMPLET depuis la racine bibliothèque (ex.
 * `2024/vacances`), jamais `''` : la racine n'est pas son propre enfant. `name` est
 * son dernier segment, fourni pour éviter que chaque appelant le re-découpe.
 */
export const folderTreeEntrySchema = z.object({
    path: z.string(),
    name: z.string(),
    count: z.number().int().nonnegative(),
    coverAssetId: z.uuid().nullable(),
    latestTakenAt: z.string().datetime({ offset: true }).nullable(),
    indexed: z.boolean(),
    hasSubfolders: z.boolean(),
});
/**
 * Réponse de `GET /api/libraries/:id/folder-tree` : les sous-répertoires DIRECTS de
 * `parent`, triés par `path`.
 *
 * `truncated` signale que le répertoire courant contenait plus de
 * `FOLDER_TREE_CHILDREN_MAX` sous-répertoires et que la liste a été TRONQUÉE. C'est
 * un garde-fou d'UI, pas une pagination : un répertoire à plus de 2 000 sous-dossiers
 * n'existe pas dans une bibliothèque photo rangée par un humain, mais il existe très
 * bien sur un disque qui a mal tourné, et il ne doit pas figer l'onglet Dossiers.
 */
export const folderTreeResultSchema = z.object({
    /** Dossier courant (chemin relatif POSIX ; `''` = racine bibliothèque). */
    parent: z.string(),
    folders: z.array(folderTreeEntrySchema),
    truncated: z.boolean(),
});
/** Nombre maximal de sous-répertoires rendus pour un niveau (garde-fou, cf. ci-dessus). */
export const FOLDER_TREE_CHILDREN_MAX = 2000;
/**
 * Query de `GET /api/libraries/:id/folder-tree`. `parent` (optionnel) = chemin
 * RELATIF du dossier courant dont on veut les sous-répertoires directs ; absent ou
 * vide ⇒ RACINE.
 *
 * ⚠ AUCUN `.transform` ici, et il ne faut JAMAIS en ajouter : un `.transform` dans un
 * schéma de corps ou de query casse `z.toJSONSchema` au démarrage et met l'API en
 * boucle de redémarrage (régression réelle en v0.70.0). La normalisation du chemin se
 * fait dans le HANDLER. Garde : `route-json-schema.test.ts`.
 *
 * La borne de longueur est la même que `folderFacetQuerySchema` (1024) : le chemin
 * n'est jamais concaténé — il transite comme paramètre lié côté SQL et par `safeJoin`
 * côté disque.
 */
export const folderTreeQuerySchema = z.object({
    parent: z.string().max(1024).optional(),
});
//# sourceMappingURL=folder-tree.js.map